import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ClassroomOptionDTO, MentorOptionDTO, RecoveryCountsDTO, RecoveryRowDTO, RecoveryScheduleBody, RecoveryStatus } from "../../../../models/StudentProgressDTO";
import { getErrorMessage } from "../../../../common";
import { fechaBonita } from "../../../../utils/fechaBonita";
import { horarioBonito } from "../../../../utils/horarioBonito";
import { montoBonito } from "../../../../utils/monto";
import S from "./strings";

import "../enrollmentUpdates/styles.css";

type Llave = "open" | "0" | "1" | "2" | "3";
type Modo = "programar" | "repuesta" | "exonerar";

interface HorarioOpcion { id: number; name: string; courseId: number; course: string; classroomId: number | null; classroom: string | null; }

const POR_PAGINA = 100;

const ESTADO_CLASE: { [k in RecoveryStatus]: string } = { 0: "waPill oro", 1: "waPill naranja", 2: "waPill verde", 3: "waPill suave" };

const contar = (c: RecoveryCountsDTO | null, k: Llave): number => {
	if (!c) return 0;
	return { open: c.open, "0": c.pending, "1": c.scheduled, "2": c.recovered, "3": c.exempted }[k] || 0;
};

/* "14:30" -> "2:30 pm" */
const horaBonita = (hhmm: string | null | undefined): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return "";
	const h = Number(m[1]);
	return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};

const normalizar = (t: any): string => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

interface ModalProps {
	campusId: number;
	modo: Modo;
	row: RecoveryRowDTO;
	horarios: HorarioOpcion[];
	aulasPlantel: ClassroomOptionDTO[];
	maestras: MentorOptionDTO[];
	moneda: string;
	onClose: () => void;
	onDone: (fila: RecoveryRowDTO, texto: string) => void;
}

/** Programar / Marcar repuesta / Exonerar una sesión por reponer. */
const AccionModal: React.FC<ModalProps> = ({ campusId, modo, row, horarios, aulasPlantel, maestras, moneda, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const delCurso = horarios.filter(h => h.courseId === row.courseId);
	const yaProgramada = Boolean(row.recoveryClass);
	//Repuesta de una Programada: basta confirmar; sin sesión hay que decir dónde se repuso.
	const pideSesion = modo === "programar" || (modo === "repuesta" && !yaProgramada);
	const precioBase = row.charge ? row.charge.price : row.suggestedPrice;
	const [scheduleId, setScheduleId] = useState<number>(row.recoveryClass?.scheduleId || row.scheduleId);
	const [date, setDate] = useState<string>(modo === "programar" ? (row.recoveryClass?.date || "") : "");
	const [time, setTime] = useState<string>(modo === "programar" ? (row.recoveryClass?.time || "").slice(0, 5) : "");
	const [classroomId, setClassroomId] = useState<number | null>(null);
	const [mentorId, setMentorId] = useState<number | null>(null);
	const [cobra, setCobra] = useState<boolean>(row.charge ? true : (precioBase || 0) > 0);
	const [precio, setPrecio] = useState<string>(precioBase != null ? String(precioBase) : "");
	const [nota, setNota] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	//Todas las aulas del plantel (GET /classrooms?all=1); sin respuesta, las de los horarios activos.
	const aulas = useMemo(() => {
		const m = new Map<number, string>();
		if (aulasPlantel.length > 0) {
			aulasPlantel.filter(a => a.statusId === 1).forEach(a => m.set(a.id, a.buildingName ? `${a.name} · ${a.buildingName}` : a.name));
			return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
		}
		horarios.forEach(h => { if (h.classroomId && h.classroom) m.set(h.classroomId, h.classroom); });
		return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
	}, [horarios, aulasPlantel]);

	const listo = modo === "exonerar" ? nota.trim().length >= 3 : (!pideSesion || Boolean(date)) && (!cobra || precio !== "");

	const guardar = async () => {
		setBusy(true);
		setError(null);
		try {
			let r;
			if (modo === "exonerar") {
				r = await http.studentProgressService.waiveRecovery(campusId, row.id, nota.trim());
			} else {
				const body: RecoveryScheduleBody = { id: row.id, charge: cobra };
				if (cobra && precio !== "") body.price = Number(precio);
				if (nota.trim()) body.note = nota.trim();
				if (pideSesion) {
					body.scheduleId = scheduleId;
					body.date = date;
					if (time) body.time = time;
					if (classroomId) body.classroomId = classroomId;
					if (mentorId) body.mentorId = mentorId;
				}
				r = modo === "programar"
					? await http.studentProgressService.scheduleRecovery(campusId, body)
					: await http.studentProgressService.markRecovered(campusId, body);
			}
			onDone(r.data, modo === "programar" ? S.recScheduledOk : modo === "repuesta" ? S.recRecoveredOk : S.recWaivedOk);
		} catch (e) {
			setError(getErrorMessage(e));
		} finally {
			setBusy(false);
		}
	};

	const titulo = modo === "programar" ? S.recSchedule : modo === "repuesta" ? S.recMarkRecovered : S.recWaive;
	return (
		<div className="wau-modal-fondo" role="dialog" aria-modal="true" onClick={onClose}>
			<div className="wau-modal" onClick={e => e.stopPropagation()}>
				<h3>{titulo} · {row.studentName}</h3>
				<p className="wau-sec" style={{ fontSize: 13.5, marginTop: -6 }}>
					{S.recSession} {row.sessionNumber ?? "—"} · {horarioBonito(row.scheduleName)}
					{row.missedClass?.date ? " · " + fechaBonita(row.missedClass.date) : ""}
				</p>
				{
					modo === "repuesta" && yaProgramada && row.recoveryClass ?
						<div className="wau-wa">{fechaBonita(row.recoveryClass.date)} {horaBonita(row.recoveryClass.time)} · {horarioBonito(row.recoveryClass.scheduleName)}{row.recoveryClass.classroomName ? " · " + row.recoveryClass.classroomName : ""}</div>
						: null
				}
				{
					!pideSesion ? null :
						<div className="wau-dos" style={{ marginTop: 0 }}>
							<label className="wau-campo" style={{ gridColumn: "1 / -1" }}><span className="wau-lab">{S.recScheduleField}</span>
								<select className="wau-entrada" value={scheduleId} onChange={e => setScheduleId(Number(e.target.value))}>
									{delCurso.some(h => h.id === row.scheduleId) ? null : <option value={row.scheduleId}>{horarioBonito(row.scheduleName)}</option>}
									{delCurso.map(h => <option key={h.id} value={h.id}>{horarioBonito(h.name)}{h.classroom ? " · " + h.classroom : ""}</option>)}
								</select>
							</label>
							<label className="wau-campo"><span className="wau-lab">{S.recDate}</span>
								<input className="wau-entrada" type="date" value={date} onChange={e => setDate(e.target.value)} />
							</label>
							<label className="wau-campo"><span className="wau-lab">{S.recTime}</span>
								<input className="wau-entrada" type="time" value={time} onChange={e => setTime(e.target.value)} />
							</label>
							<label className="wau-campo"><span className="wau-lab">{S.recClassroom}</span>
								<select className="wau-entrada" value={classroomId ?? ""} onChange={e => setClassroomId(Number(e.target.value) || null)}>
									<option value="">{S.recFromSchedule}</option>
									{aulas.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
								</select>
							</label>
							<label className="wau-campo"><span className="wau-lab">{S.recMentor}</span>
								<select className="wau-entrada" value={mentorId ?? ""} onChange={e => setMentorId(Number(e.target.value) || null)}>
									<option value="">{S.recFromSchedule}</option>
									{maestras.filter(m => m.enabled).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
								</select>
							</label>
						</div>
				}
				{
					modo === "exonerar" ? null :
						<div className="wau-fila" style={{ marginTop: 12 }}>
							<label className="wau-opcion" style={{ gridTemplateColumns: "22px auto", alignItems: "center" }}>
								<input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} style={{ marginTop: 0, gridRow: "auto" }} />
								<span className="t">{S.recCharge}</span>
							</label>
							{
								!cobra ? null :
									<label className="wau-campo" style={{ width: 160 }}><span className="wau-lab">{S.recPrice} ({moneda})</span>
										<input className="wau-entrada wau-num" type="number" min={0} step={0.01} value={precio} onChange={e => setPrecio(e.target.value)} style={{ textAlign: "right" }} />
									</label>
							}
						</div>
				}
				<label className="wau-campo" style={{ marginTop: 12 }}><span className="wau-lab">{S.recNote}</span>
					<textarea className="wau-entrada" rows={2} value={nota} onChange={e => setNota(e.target.value)} />
				</label>
				{error ? <p className="wau-adv" style={{ marginTop: 8 }}>{error}</p> : null}
				<div className="wau-acciones">
					<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.recBack}</button>
					<button type="button" className="wau-btn p" disabled={busy || !listo} onClick={guardar}>{titulo}</button>
				</div>
			</div>
		</div>
	);
};

/** Pestaña "Por reponer" de Progreso Académico (FASE 3). */
const PorReponerTab: React.FC<{ campusId: number; moneda: string; idleLocked: boolean; onChanged: () => void }> = ({ campusId, moneda, idleLocked, onChanged }) => {
	const http = HttpManager.getInstance();
	const [llave, setLlave] = useState<Llave>("open");
	const [filas, setFilas] = useState<RecoveryRowDTO[] | null>(null);
	const [cuentas, setCuentas] = useState<RecoveryCountsDTO | null>(null);
	const [buscar, setBuscar] = useState<string>("");
	const [horarios, setHorarios] = useState<HorarioOpcion[]>([]);
	const [maestras, setMaestras] = useState<MentorOptionDTO[]>([]);
	const [accion, setAccion] = useState<{ modo: Modo; row: RecoveryRowDTO } | null>(null);
	const [aviso, setAviso] = useState<{ text: string; ok: boolean } | null>(null);
	const [tick, setTick] = useState<number>(0);
	const [aulasPlantel, setAulasPlantel] = useState<ClassroomOptionDTO[]>([]);
	const [cursoId, setCursoId] = useState<number>(0);
	const [pagina, setPagina] = useState<number>(1);
	const [paginas, setPaginas] = useState<{ total: number; last: number }>({ total: 0, last: 1 });
	//Lo escrito se manda al servidor (q) medio segundo después de la última tecla (R2); la página vuelve a 1.
	const [q, setQ] = useState<string>("");

	useEffect(() => {
		const t = window.setTimeout(() => { if (buscar.trim() !== q) { setQ(buscar.trim()); setPagina(1); } }, 500);
		return () => window.clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buscar]);

	useEffect(() => {
		let vigente = true;
		setFilas(null);
		http.studentProgressService.getRecoveries(campusId, { status: llave, courseId: cursoId || undefined, q: q || undefined, page: pagina, perPage: POR_PAGINA })
			.then(r => {
				if (!vigente) return;
				setFilas(Array.isArray(r.data?.rows) ? r.data.rows : []);
				setCuentas(r.data?.counts || null);
				setPaginas({ total: r.data?.total ?? (r.data?.rows || []).length, last: r.data?.lastPage || 1 });
			})
			.catch(e => { if (vigente) { setFilas([]); setAviso({ text: getErrorMessage(e), ok: false }); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, llave, cursoId, q, pagina, tick]);

	useEffect(() => {
		let vigente = true;
		http.scheduleService.getSchedulesByCampusId(campusId, 1000, { statusId: "1" })
			.then((r: any) => vigente && setHorarios((r.data?.data || []).map((s: any) => ({
				id: s.scheduleId, name: s.name, courseId: s.courseId, course: s.course || "", classroomId: s.classroomId ?? null, classroom: s.classroom ?? null
			}))))
			.catch(() => { /* sin horarios: solo el de la alumna */ });
		http.studentProgressService.getMentors(campusId)
			.then(r => vigente && setMaestras(Array.isArray(r.data) ? r.data : []))
			.catch(() => { /* sin maestras: la del horario */ });
		http.studentProgressService.getClassrooms(campusId)
			.then(r => vigente && setAulasPlantel(Array.isArray(r.data) ? r.data : []))
			.catch(() => { /* sin permiso de aulas: las de los horarios */ });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId]);

	//Mientras el servidor no filtre por q, la página cargada se filtra aquí con lo mismo.
	const visibles = useMemo(() => {
		const texto = normalizar(buscar.trim());
		const base = filas || [];
		return !texto ? base : base.filter(r => normalizar([r.studentName, r.identityCode, r.courseName, r.scheduleName, horarioBonito(r.scheduleName)].join(" ")).includes(texto));
	}, [filas, buscar]);

	const cursos = useMemo(() => {
		const m = new Map<number, string>();
		horarios.forEach(h => { if (h.courseId && h.course) m.set(h.courseId, h.course); });
		return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "es"));
	}, [horarios]);

	const chips: { k: Llave; t: string }[] = [
		{ k: "open", t: S.recOpen }, { k: "0", t: S.recStatus[0] }, { k: "1", t: S.recStatus[1] }, { k: "2", t: S.recStatus[2] }, { k: "3", t: S.recStatus[3] }
	];

	const cobro = (r: RecoveryRowDTO) => {
		if (r.charge) {
			const pagado = r.charge.paid >= r.charge.total && r.charge.total > 0;
			return <><div className="wau-num">{moneda} {montoBonito(r.charge.total)}</div><div className={pagado ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{pagado ? S.recPaid : S.recUnpaid}</div></>;
		}
		if (r.status === 3) return <span className="wau-sec">—</span>;
		return r.suggestedPrice ? <span className="wau-sec wau-num">{moneda} {montoBonito(r.suggestedPrice)}</span> : <span className="wau-sec">{S.recNoCharge}</span>;
	};

	return (
		<div className="wau" style={{ padding: 0 }}>
			<div className="wau-chips" role="group">
				{chips.map(c => (
					<button key={c.k} type="button" className="wau-chip" aria-pressed={llave === c.k} disabled={idleLocked} onClick={() => { setLlave(c.k); setPagina(1); }}>
						{c.t} <span className="wau-num">({contar(cuentas, c.k)})</span>
					</button>
				))}
				<select className="wau-entrada" style={{ width: 240, marginLeft: "auto" }} aria-label={S.recColCourse} value={cursoId}
					onChange={e => { setCursoId(Number(e.target.value) || 0); setPagina(1); }}>
					<option value={0}>{S.recAllCourses}</option>
					{cursos.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
				</select>
				<input type="search" className="wau-entrada" style={{ width: 240 }} placeholder={S.recSearch} value={buscar} onChange={e => setBuscar(e.target.value)} />
			</div>
			{aviso ? <div className={"wau-aviso " + (aviso.ok ? "ok" : "mal")} role="status"><span>{aviso.text}</span><button type="button" aria-label={S.recClose} onClick={() => setAviso(null)}>×</button></div> : null}
			{
				filas === null ? <div className="wau-caja wau-sec">{S.recLoading}</div> :
					<div className="wau-caja wau-tabla">
						<table className="wpr-tabla">
							<thead className="wpr-cab"><tr><th>{S.recColStudent}</th><th>{S.recColCourse}</th><th>{S.recColSession}</th><th>{S.recColStatus}</th><th>{S.recColRecovery}</th><th className="der">{S.recColCharge}</th><th>{S.recColNote}</th><th></th></tr></thead>
							<tbody>
								{visibles.length === 0 ? <tr><td colSpan={8} className="wau-sec" style={{ textAlign: "center", padding: 20 }}>{S.recEmpty}</td></tr> : visibles.map(r => {
									const abierta = r.status === 0 || r.status === 1;
									return (
										<tr key={r.id}>
											<td><b>{r.studentName}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{r.identityCode}</div></td>
											<td>{r.courseName}<div className="wau-sec" style={{ fontSize: 12.5 }}>{horarioBonito(r.scheduleName)}</div></td>
											<td className="wau-num" style={{ whiteSpace: "nowrap" }}>
												{S.recSession} {r.sessionNumber ?? "—"}
												<div className="wau-sec" style={{ fontSize: 12.5 }}>{[r.originLabel, r.missedClass?.date ? fechaBonita(r.missedClass.date) : null].filter(Boolean).join(" · ")}</div>
											</td>
											<td><span className={ESTADO_CLASE[r.status] || "waPill suave"}>{S.recStatus[r.status] || r.statusLabel}</span></td>
											<td style={{ fontSize: 13.5 }}>
												{
													!r.recoveryClass ? <span className="wau-sec">—</span> : <>
														<div className="wau-num">{fechaBonita(r.recoveryClass.date)} {horaBonita(r.recoveryClass.time)}</div>
														<div className="wau-sec" style={{ fontSize: 12.5 }}>{[horarioBonito(r.recoveryClass.scheduleName), r.recoveryClass.classroomName, r.recoveryClass.mentorName].filter(Boolean).join(" · ")}</div>
													</>
												}
											</td>
											<td className="der">{cobro(r)}</td>
											<td style={{ fontSize: 13, maxWidth: 220 }}>{r.note || ""}</td>
											<td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
												{
													!abierta ? null : <>
														<button type="button" className="wau-btn s mini" disabled={idleLocked} onClick={() => setAccion({ modo: "programar", row: r })}>{r.status === 1 ? S.recReschedule : S.recSchedule}</button>{" "}
														<button type="button" className="wau-btn s mini" disabled={idleLocked} onClick={() => setAccion({ modo: "repuesta", row: r })}>{S.recMarkRecovered}</button>{" "}
														<button type="button" className="wau-btn s mini" disabled={idleLocked} onClick={() => setAccion({ modo: "exonerar", row: r })}>{S.recWaive}</button>
													</>
												}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						{
							paginas.last <= 1 ? null :
								<div className="wau-acciones">
									<span className="wau-sec wau-num">{S.recPage(pagina, paginas.last, paginas.total)}</span>
									<button type="button" className="wau-btn s mini" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>‹</button>
									<button type="button" className="wau-btn s mini" disabled={pagina >= paginas.last} onClick={() => setPagina(p => p + 1)}>›</button>
								</div>
						}
					</div>
			}
			{
				!accion ? null :
					<AccionModal campusId={campusId} modo={accion.modo} row={accion.row} horarios={horarios} aulasPlantel={aulasPlantel} maestras={maestras} moneda={moneda}
						onClose={() => setAccion(null)}
						onDone={(_fila, texto) => { setAccion(null); setAviso({ text: texto, ok: true }); setTick(t => t + 1); onChanged(); }}
					/>
			}
		</div>
	);
};

export default PorReponerTab;
